import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

// A lightweight, dependency-free searchable dropdown. Shows a search box inside
// the opened menu, filters as you type, and lists options alphabetically (A→Z).
// Keeps the Bootstrap "form-select" look so it blends with the existing form.
const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  getOptionValue = (o) => o._id,
  getOptionLabel = (o) => o.designation || '',
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  loading = false,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Filter by the search text, then sort alphabetically ascending.
  const sortedFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...options]
      .filter((o) => {
        if (!q) return true;
        return String(getOptionLabel(o)).toLowerCase().includes(q);
      })
      .sort((a, b) =>
        String(getOptionLabel(a)).localeCompare(String(getOptionLabel(b)), undefined, {
          sensitivity: 'base',
          numeric: true,
        })
      );
  }, [options, query, getOptionLabel]);

  const selected = options.find((o) => String(getOptionValue(o)) === String(value));
  const selectedLabel = selected ? getOptionLabel(selected) : '';

  return (
    <div className="position-relative" ref={containerRef}>
      <button
        type="button"
        id={id}
        className="form-select text-start d-flex align-items-center justify-content-between"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedLabel ? '' : 'text-muted'}>
          {loading ? 'Loading officers...' : selectedLabel || placeholder}
        </span>
      </button>

      {open && !disabled && (
        <div
          className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
          style={{ zIndex: 1056, maxHeight: 260, overflowY: 'auto' }}
        >
          <div className="p-2 border-bottom bg-white" style={{ position: 'sticky', top: 0 }}>
            <div className="position-relative">
              <Icon
                icon="mdi:magnify"
                style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#888' }}
              />
              <input
                type="text"
                autoFocus
                className="form-control form-control-sm ps-4"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
          <ul className="list-unstyled mb-0">
            {sortedFiltered.length === 0 ? (
              <li className="px-3 py-2 text-muted small">No matches found.</li>
            ) : (
              sortedFiltered.map((o) => {
                const val = getOptionValue(o);
                const isSel = String(val) === String(value);
                return (
                  <li key={val}>
                    <button
                      type="button"
                      className={`dropdown-item px-3 py-2 w-100 text-start ${isSel ? 'active bg-success text-white' : ''}`}
                      onClick={() => {
                        onChange(String(val));
                        setOpen(false);
                        setQuery('');
                      }}
                    >
                      {getOptionLabel(o)}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
